import { Box } from "@mui/material";
import React, { FC } from "react";
import { useUser } from "../../../../../hooks/useUser";
import { useNetworkScope } from "../../../../../hooks/useNetworkScope";
import { isInternal } from "../../../../../utils";
import { VideoSection } from "../../../../../components/videoSection/VideoSection";

/**
 * The product page's video list: a copy of productDetail/ProductVideos.tsx
 * (kept untouched for rollback) with the same VideoSection props, plus:
 * - `onCountChange` + `hideCount`: the section heading's pill shows the
 *   list's total, so the list's own "{n} videos" line is dropped (only the
 *   total — the chips row stays for filters and the Non-project toggle).
 * - `nonProjectToggleTarget`: the brand's "Non-project only" toggle is drawn in
 *   the section heading (left of Videos | Stories) instead of its own row.
 * - `!px-0` on the inline video view: it wraps itself in a MainContainer
 *   (px-3 sm:px-6), which this page already sits in, so without it an open
 *   video is inset 12/24px from the header and the other sections.
 * - The wrapper has no top margin (mt-6 before): VideosSection's tabpanel
 *   spaces the list under the new heading instead of the old Videos | Stories
 *   button.
 * - `onOrder`: the empty state's button opens the order modal VideosSection
 *   holds (it no longer remounts with this list on every tab switch).
 */

interface Props {
  /** The empty state's Request a quote / New order: opens VideosSection's order modal. */
  onOrder: () => void;
  /** VideoSection's list total (0 while loading; see its useEffect). */
  onCountChange?: (count: number) => void;
  /** The heading element the Non-project toggle is drawn into (null until mounted). */
  nonProjectToggleTarget?: HTMLElement | null;
}

export const ProductVideoList: FC<Props> = ({ onOrder, onCountChange, nonProjectToggleTarget }) => {
  const { role } = useUser();
  // Workspace-aware (TDC-1596): the opened brand inside /portal/:slug, else
  // the user's own brand.
  const { brandId } = useNetworkScope();

  return (
    <Box>
      <VideoSection
        brandId={brandId || undefined}
        defaultView="grid"
        features={{
          sort: true,
          filters: true,
          // Internal ('off'): the API skips nonProjectOnly whenever
          // productId is present, so the toggle would be a no-op.
          nonProject: brandId ? "probe" : "off",
          inlineDetail: true,
        }}
        isProductVideos
        excludeFilterKeys={["productId", "brandId"]}
        emptyTitle="No videos with this product yet"
        emptyBody="Create an order to produce a video with this product."
        emptyCta={{
          label: isInternal(role) ? "New order" : "Request a quote",
          onClick: onOrder,
        }}
        detailContainerClassName="!py-0 !px-0"
        onCountChange={onCountChange}
        hideCount
        nonProjectToggleTarget={nonProjectToggleTarget}
      />
    </Box>
  );
};
